import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';

const ProtectedRoute = ({ children, requireAuth = true, redirectTo = '/login' }) => {
  const { t } = useTranslation();
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth state
  if (loading) {
    return (
      <div className="min-h-screen  flex items-center justify-center">
        <LoadingSpinner size="xl" logoSpinner={true} text={t('common.loading', 'Loading...')} />
      </div>
    );
  }

  // If route requires authentication and user is not logged in
  if (requireAuth && !currentUser) {
    // Save the attempted location for redirecting after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // If route requires authentication but user hasn't verified email
  if (requireAuth && currentUser && !currentUser.emailVerified) {
    // Google sign-in users are automatically verified, so only check for email/password users
    const isGoogleUser = currentUser.providerData?.some(provider => provider.providerId === 'google.com');
    if (!isGoogleUser) {
      return <Navigate to="/verify-email" state={{ from: location }} replace />;
    }
  }

  // If route is for guests only (like login/register) and user is logged in
  if (!requireAuth && currentUser) {
    // If user is not verified, send to verify-email page
    if (!currentUser.emailVerified) {
      const isGoogleUser = currentUser.providerData?.some(provider => provider.providerId === 'google.com');
      if (!isGoogleUser) {
        return <Navigate to="/verify-email" state={{ from: location }} replace />;
      }
    }
    // Redirect authenticated users to the originally intended page or redirectTo
    const from = location.state?.from?.pathname || redirectTo || '/dashboard';
    return <Navigate to={from} replace />;
  }

  // Render the protected component
  return children;
};

export default ProtectedRoute; 